import { useCallback, useEffect, useRef, useState } from "react";
import { SITE } from "@/lib/site";
import { cn } from "@/lib/utils";

/**
 * Email link with a no-mail-client fallback.
 *
 * A bare `mailto:` does nothing on devices without a configured mail handler,
 * which reads as a dead click. If the page is still focused shortly after the
 * click, we copy the address to the clipboard and confirm inline instead.
 */
export function EmailContactLink({
  className,
  children,
  copiedLabel = "Address copied",
}: {
  className?: string;
  children?: React.ReactNode;
  copiedLabel?: string;
}) {
  const [copied, setCopied] = useState(false);
  const timers = useRef<number[]>([]);

  useEffect(
    () => () => {
      timers.current.forEach((t) => window.clearTimeout(t));
    },
    [],
  );

  const handleClick = useCallback(() => {
    // If a mail client opens, the tab is blurred or hidden. If neither happens
    // the click did nothing, so fall back to copying the address.
    let left = false;
    const mark = () => {
      left = true;
    };
    window.addEventListener("blur", mark, { once: true });
    document.addEventListener("visibilitychange", mark, { once: true });

    const check = window.setTimeout(() => {
      window.removeEventListener("blur", mark);
      document.removeEventListener("visibilitychange", mark);
      if (left || document.hidden) return;
      const done = () => {
        setCopied(true);
        timers.current.push(window.setTimeout(() => setCopied(false), 2500));
      };
      const legacyCopy = () => {
        try {
          const field = document.createElement("textarea");
          field.value = SITE.email;
          field.setAttribute("readonly", "");
          field.style.position = "fixed";
          field.style.opacity = "0";
          document.body.appendChild(field);
          field.select();
          document.execCommand("copy");
          document.body.removeChild(field);
        } catch {
          /* clipboard unavailable - the link text already shows the address */
        }
        done();
      };
      try {
        const copy = navigator.clipboard?.writeText(SITE.email);
        if (copy) {
          void copy.then(done, legacyCopy);
        } else {
          legacyCopy();
        }
      } catch {
        legacyCopy();
      }
    }, 900);
    timers.current.push(check);
  }, []);


  return (
    <a
      href={SITE.emailHref}
      onClick={handleClick}
      title={`Email ${SITE.email}`}
      aria-label={`Email ${SITE.email}`}
      className={cn("transition-colors", className)}
    >
      {copied ? copiedLabel : (children ?? SITE.email)}
    </a>
  );
}
